import { useRef } from "react";
import { Button } from "@/components/ui/Button";
import { Input, Field } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import {
  useGoogleMapsAutocomplete,
  type PlacesSelection,
  type PlacesStatus,
} from "../places";
import type { PropertyInfoDto } from "../api";
import type { PropertyHeader } from "./types";

interface PropertyInfoFormProps {
  data: PropertyHeader;
  onPatch: (patch: Partial<PropertyHeader>) => void;
  /** Objection flow records field corrections on blur; PAA omits this. */
  onFieldBlur?: (field: keyof PropertyHeader) => void;
  savedInfo?: PropertyInfoDto | null;
  savedInfoSearching?: boolean;
  onApplySavedInfo?: () => void;
  googleMapsApiKey?: string | null;
  /** True while the page is auto-resolving the address via Google Places. */
  addressVerifying?: boolean;
}

// The shared "Property" section for BOTH the Objection and PAA review pages:
// address (with Google autocomplete + corner-lot candidate chooser),
// Block/Lot/BIN/Job/CB/Applicant, and the "saved property information" panel.
// Extracted so the two flows stay in lockstep (they had drifted — PAA had the
// chooser/ambiguity handling the objection flow lacked).
export function PropertyInfoForm({
  data,
  onPatch,
  onFieldBlur,
  savedInfo,
  savedInfoSearching,
  onApplySavedInfo,
  googleMapsApiKey,
  addressVerifying = false,
}: PropertyInfoFormProps) {
  const addressInputRef = useRef<HTMLInputElement>(null);

  const placesStatus: PlacesStatus = useGoogleMapsAutocomplete(
    addressInputRef,
    googleMapsApiKey,
    (sel: PlacesSelection) => {
      onPatch({
        address: sel.formatted,
        houseNumber: sel.houseNumber,
        streetName: sel.streetName,
        borough: sel.borough,
      });
    },
  );

  const addressHelper = (() => {
    if (addressVerifying) return "Verifying with Google Maps…";
    switch (placesStatus) {
      case "no-key":
        return "Manual entry — Google Maps key not configured";
      case "loading":
        return "Loading Maps…";
      case "ready":
        return "Start typing to see suggestions";
      case "selected":
        return "Address verified";
      case "error":
        return "Autocomplete unavailable";
      default:
        return "NYC address";
    }
  })();

  const candidates = (data.addressCandidates ?? []).filter((c) => c.trim());
  const showCandidates = candidates.length >= 2;
  const lotCandidates = (data.lotCandidates ?? []).filter((c) => c.trim());
  const currentAddr = (data.address ?? "").trim().toLowerCase();
  const isActiveCandidate = (c: string) => {
    const cl = c.toLowerCase();
    return currentAddr === cl || currentAddr.startsWith(cl) || currentAddr.includes(cl);
  };

  return (
    <section className="rounded-lg border border-rule bg-surface shadow-soft">
      <header className="border-b border-rule px-6 py-3">
        <div className="text-[0.95rem] font-semibold text-ink">Property</div>
      </header>
      <div className="grid grid-cols-12 gap-x-5 gap-y-5 px-6 py-6">
        <div className="col-span-12">
          <Field label="Property Address" required helper={addressHelper}>
            <Input
              ref={addressInputRef}
              id="address-input"
              value={data.address ?? ""}
              onChange={(e) => onPatch({ address: e.target.value })}
              onBlur={() => onFieldBlur?.("address")}
              placeholder="123 Main Street, Brooklyn"
              required
              autoComplete="off"
            />
          </Field>
          {showCandidates && (
            // Corner lots surface two valid site addresses (filename vs the
            // title-block project address). Offer them inline; picking one just
            // sets the address (Google Places + saved-info re-run off it).
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <span className="text-[0.8rem] text-ink-soft">
                {candidates.length} possible site addresses — pick one:
              </span>
              {candidates.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onPatch({ address: c })}
                  aria-pressed={isActiveCandidate(c)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-[0.8rem] transition-colors",
                    isActiveCandidate(c)
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-rule bg-surface text-ink-soft hover:border-primary/50 hover:text-ink",
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-6 sm:col-span-3">
          <Field label="Block">
            <Input
              value={data.block ?? ""}
              onChange={(e) => onPatch({ block: e.target.value })}
              onBlur={() => onFieldBlur?.("block")}
            />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Field label="Lot">
            <Input
              value={data.lot ?? ""}
              onChange={(e) => onPatch({ lot: e.target.value })}
              onBlur={() => onFieldBlur?.("lot")}
            />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Field label="BIN" helper="Building ID Number">
            <Input
              value={data.bin ?? ""}
              onChange={(e) => onPatch({ bin: e.target.value })}
              onBlur={() => onFieldBlur?.("bin")}
            />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Field label="Job Number" required>
            <Input
              value={data.jobNumber ?? ""}
              onChange={(e) => onPatch({ jobNumber: e.target.value })}
              onBlur={() => onFieldBlur?.("jobNumber")}
              required
            />
          </Field>
        </div>

        {lotCandidates.length >= 2 && (
          // Extraction found several possible lots (e.g. a plan set that lists
          // neighboring lots on the site plan) and couldn't confidently pick
          // one. Offer them rather than guess wrong — same pattern as the
          // corner-lot address chooser above.
          <div className="col-span-12 -mt-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[0.8rem] text-ink-soft">
                Couldn't determine the lot — {lotCandidates.length} candidates, pick one:
              </span>
              {lotCandidates.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onPatch({ lot: c })}
                  aria-pressed={(data.lot ?? "").trim() === c}
                  className={cn(
                    "rounded-full border px-3 py-1 text-[0.8rem] transition-colors",
                    (data.lot ?? "").trim() === c
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-rule bg-surface text-ink-soft hover:border-primary/50 hover:text-ink",
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="col-span-12 sm:col-span-6">
          <Field label="CB No." helper="Community Board Number">
            <Input
              value={data.cbNumber ?? ""}
              onChange={(e) => onPatch({ cbNumber: e.target.value })}
              onBlur={() => onFieldBlur?.("cbNumber")}
            />
          </Field>
        </div>
        <div className="col-span-12 sm:col-span-6">
          <Field label="Applicant Name">
            <Input
              value={data.applicantName ?? ""}
              onChange={(e) => onPatch({ applicantName: e.target.value })}
              onBlur={() => onFieldBlur?.("applicantName")}
            />
          </Field>
        </div>

        {(savedInfoSearching || savedInfo) && (
          <div className="col-span-12">
            {savedInfoSearching && !savedInfo && (
              <div className="rounded-md bg-canvas-2 px-4 py-3 text-[0.88rem] text-ink-muted">
                Searching for saved property information…
              </div>
            )}
            {savedInfo && (
              <div className="rounded-md border border-sky/30 bg-sky-soft px-4 py-3">
                <div className="text-[0.85rem] font-medium text-sky-ink">
                  Found saved property information
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {savedInfo.cbNumber && <Chip>CB: {savedInfo.cbNumber}</Chip>}
                  {savedInfo.bin && <Chip>BIN: {savedInfo.bin}</Chip>}
                  {savedInfo.applicantName && (
                    <Chip>Applicant: {savedInfo.applicantName}</Chip>
                  )}
                  {onApplySavedInfo && (
                    <Button intent="soft" size="sm" onClick={onApplySavedInfo}>
                      ✓ Apply saved info
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-sky/40 bg-surface px-2.5 py-0.5 text-[0.78rem] font-medium text-sky-ink">
      {children}
    </span>
  );
}
